import { ReactElement, useContext, useEffect, useMemo, useRef } from 'react';
import { StoresContext } from '../App';
import { compareStrings } from '../util/compareStrings';
import { EXCLUDED_REGIONS } from '../config';

type RenderComponent = (props: {
    country: string;
    region: string;
    city: string;
    countries: string[];
    regions: string[];
    cities: string[];
    setCountry: (v: string) => void;
    setRegion: (v: string) => void;
    setCity: (v: string) => void;
}) => ReactElement;

export default function LocationFilters({ render }: { render: RenderComponent }) {
    const { allStores, locationFilters, setLocationFilters } = useContext(StoresContext);

    const options = useMemo(() => {
        const countries = new Set<string>();
        const regions = new Set<string>();
        const cities = new Set<string>();
        for (const s of allStores) {
            if (s.country) {
                countries.add(s.country);
            }
            if (locationFilters.country && s.region && s.country === locationFilters.country) {
                if (EXCLUDED_REGIONS.includes(s.region)) continue
                regions.add(s.region);
            }
            if (
                locationFilters.country &&
                locationFilters.region &&
                s.city &&
                s.country === locationFilters.country &&
                s.region === locationFilters.region
            ) {
                cities.add(s.city);
            }
        }
        return {
            countries: Array.from(countries).sort(compareStrings),
            regions: Array.from(regions).sort(compareStrings),
            cities: Array.from(cities).sort(compareStrings),
        };
    }, [allStores, locationFilters]);

    // сброс фильтров при поиске
    const hasMountedPage = useRef(false);
    useEffect(() => {
        if (!hasMountedPage.current) {
            hasMountedPage.current = true;
            return;
        }
        if (options.countries.length > 0) {
            setLocationFilters({country: '', region: '', city: ''});
        }
    }, [options.countries.length, setLocationFilters])

    // сброс невозможных фильтров
    useEffect(() => {
        if (options.countries.length > 0) {
            if (locationFilters.country && !options.countries.includes(locationFilters.country)) {
                setLocationFilters({country: '', region: '', city: ''});
                return;
            }
            if (locationFilters.region && !options.regions.includes(locationFilters.region)) {
                setLocationFilters({country: locationFilters.country, region: '', city: ''});
                return;
            }
            if (locationFilters.city && !options.cities.includes(locationFilters.city)) {
                setLocationFilters({
                    country: locationFilters.country,
                    region: locationFilters.region,
                    city: '',
                });
                return;
            }
        }
    }, [options, locationFilters, setLocationFilters]);

    const setValues = (v: { country?: string; region?: string; city?: string }) => {
        setLocationFilters((values) => ({ ...values, ...v }));
    };
    const setters = {
        setCountry: (country: string) => {
            setValues({ country, region: '', city: '' });
        },
        setRegion: (region: string) => {
            setValues({ region, city: '' });
        },
        setCity: (city: string) => {
            setValues({ city });
        },
    };
    const Render = render;

    return <Render {...locationFilters} {...options} {...setters} />;
}
